// src/pages/Positionnements.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { positionnementAPI } from '../services/api';
import toast from 'react-hot-toast';
import { Search, RefreshCw, MapPin, Eye, Filter, X, Building2 } from 'lucide-react';

const ETAT_META = {
  'Positionné':             { color:'#3B82F6', bg:'rgba(59,130,246,0.12)' },
  'Qualification en cours': { color:'#F59E0B', bg:'#FFFBEB' },
  'Intercontrat':           { color:'#8B5CF6', bg:'rgba(139,92,246,0.10)' },
  'Futur arrivé':           { color:'#10B981', bg:'rgba(16,185,129,0.10)' },
  'Refusé':                 { color:'#EF4444', bg:'rgba(239,68,68,0.12)' },
  'Abandonné':              { color:'#6B7280', bg:'rgba(107,114,128,0.10)' },
};

const PAGE_SIZE = 15;

export default function Positionnements() {
  const navigate  = useNavigate();
  const [items,   setItems]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [search,    setSearch]    = useState('');
  const [etat,      setEtat]      = useState('');
  const [societe,   setSociete]   = useState('');
  const [responsable, setResponsable] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [page,      setPage]      = useState(1);
  const [skip,      setSkip]      = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await positionnementAPI.list({
        search:      search      || undefined,
        etat:        etat        || undefined,
        societe:     societe     || undefined,
        responsable: responsable || undefined,
        skip:        (page - 1) * PAGE_SIZE,
        limit:       PAGE_SIZE,
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Erreur chargement positionnements'); }
    finally { setLoading(false); }
  }, [search, etat, societe, responsable, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hasFilters = !!(etat || societe || responsable);
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); fetchData(); };
  const resetFilters = () => { setSearch(''); setEtat(''); setSociete(''); setResponsable(''); setPage(1); };

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1>Positionnements</h1>
          <p>Historique des positionnements importés depuis Boond Manager</p>
        </div>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={() => setShowFilters(s => !s)}>
            <Filter size={15}/> Filtres
            {hasFilters && (
              <span style={{ marginLeft:4, background:'#FF4100', color:'white', borderRadius:'50%', width:16, height:16, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'0.6rem' }}>
                {(etat ? 1 : 0) + (societe ? 1 : 0) + (responsable ? 1 : 0)}
              </span>
            )}
          </button>
          <button className="btn btn-secondary" onClick={() => fetchData()}><RefreshCw size={15}/></button>
        </div>
      </div>

      {/* Barre recherche */}
      <div style={{ display:'flex', gap:10, marginBottom:16 }}>
        <div style={{ position:'relative', flex:1 }}>
          <Search size={14} style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'var(--text-muted)' }}/>
          <input
            style={{ paddingLeft:34, height:38, border:'1px solid var(--border)', borderRadius:8, fontSize:'0.875rem', outline:'none', width:'100%', boxSizing:'border-box', background:'var(--bg-elevated)', color:'var(--text-primary)' }}
            placeholder="Candidat, société, besoin, référence…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch(e)}
          />
        </div>
        {(search || hasFilters) && (
          <button className="btn btn-ghost btn-sm" onClick={resetFilters}><X size={14}/> Réinitialiser</button>
        )}
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="card" style={{ marginBottom:16, padding:'14px 16px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:10 }}>
            <div>
              <label style={lbl}>État</label>
              <select className="filter-select" style={{ width:'100%' }} value={etat}
                onChange={e => { setEtat(e.target.value); setPage(1); }}>
                <option value="">Tous les états</option>
                {Object.keys(ETAT_META).map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Société</label>
              <input style={inp} placeholder="Filtrer par société…" value={societe}
                onChange={e => { setSociete(e.target.value); setPage(1); }}/>
            </div>
            <div>
              <label style={lbl}>Responsable</label>
              <input style={inp} placeholder="Filtrer par responsable…" value={responsable}
                onChange={e => { setResponsable(e.target.value); setPage(1); }}/>
            </div>
          </div>
        </div>
      )}

      <div style={{ fontSize:'0.8125rem', color:'var(--text-muted)', marginBottom:12 }}>
        {loading ? 'Chargement…' : `${total} positionnement${total !== 1 ? 's' : ''}${totalPages > 1 ? ` — page ${page}/${totalPages}` : ''}`}
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:'48px', gap:10, color:'var(--text-muted)' }}>
          <div className="spinner"/> Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="card" style={{ textAlign:'center', padding:'48px' }}>
          <MapPin size={36} style={{ opacity:0.3, marginBottom:12 }}/>
          <div style={{ fontWeight:600, marginBottom:6 }}>Aucun positionnement</div>
          <div style={{ fontSize:'0.875rem', color:'var(--text-muted)' }}>
            Les données seront importées automatiquement depuis Boond Manager au prochain redémarrage.
          </div>
        </div>
      ) : (
        <>
          <div className="card">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Réf.</th>
                    <th>Candidat / Ressource</th>
                    <th>Type</th>
                    <th>Besoin</th>
                    <th>Société</th>
                    <th>État</th>
                    <th>TJM</th>
                    <th>Responsable</th>
                    <th>Date création</th>
                    <th>Date MAJ</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(p => {
                    const em = ETAT_META[p.etat] || { color:'var(--text-muted)', bg:'rgba(255,255,255,0.06)' };
                    return (
                      <tr key={p.id} style={{ cursor:'pointer' }} onClick={() => navigate(`/positionnements/${p.id}`)}>
                        <td>
                          <span style={{ fontWeight:700, color:'#FF4100', fontSize:'0.82rem' }}>{p.reference}</span>
                        </td>
                        <td>
                          <div style={{ fontWeight:600, fontSize:'0.875rem' }}>
                            {[p.cand_prenom, p.cand_nom].filter(Boolean).join(' ') || '—'}
                          </div>
                          <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{p.cand_ref}</div>
                        </td>
                        <td>
                          <span style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{p.cand_type || '—'}</span>
                        </td>
                        <td style={{ maxWidth:200 }}>
                          <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontSize:'0.82rem' }} title={p.besoin_titre}>
                            {p.besoin_titre || '—'}
                          </div>
                          <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{p.besoin_reference}</div>
                        </td>
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:5, fontSize:'0.82rem' }}>
                            <Building2 size={12} style={{ color:'var(--text-muted)', flexShrink:0 }}/>
                            {p.societe_nom || '—'}
                          </div>
                        </td>
                        <td>
                          <span style={{ padding:'3px 10px', borderRadius:20, fontSize:'0.75rem', fontWeight:700, background:em.bg, color:em.color, whiteSpace:'nowrap' }}>
                            {p.etat || '—'}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize:'0.8125rem', fontWeight:600, color:'#EA580C' }}>
                            {p.tarif_journalier_ht ? `${p.tarif_journalier_ht}` : '—'}
                          </span>
                        </td>
                        <td style={{ fontSize:'0.75rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>
                          {p.responsable_manager_besoin || p.responsable_manager_candidat || '—'}
                        </td>
                        <td style={{ fontSize:'0.75rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>
                          {p.date_creation ? new Date(p.date_creation).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        <td style={{ fontSize:'0.75rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>
                          {p.date_maj ? new Date(p.date_maj).toLocaleDateString('fr-FR') : '—'}
                        </td>
                        <td>
                          <button className="btn btn-ghost btn-sm" onClick={() => navigate(`/positionnements/${p.id}`)}><Eye size={13}/> Voir</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:12, marginTop:16, fontSize:'0.8125rem' }}>
              <button className="btn btn-ghost btn-sm" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Préc</button>
              <span style={{ color:'var(--text-muted)' }}>Page {page} / {totalPages}</span>
              <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Suivant →</button>
            </div>
          )}
        </>
      )}

    </div>
  );
}

const lbl = { display:'block', fontSize:'0.75rem', fontWeight:600, color:'var(--text-muted)', marginBottom:4, textTransform:'uppercase', letterSpacing:'0.04em' };
const inp = { width:'100%', padding:'8px 10px', border:'1.5px solid var(--border)', borderRadius:7, fontSize:'0.875rem', boxSizing:'border-box', fontFamily:'inherit', outline:'none', background:'var(--bg-elevated)', color:'var(--text-primary)' };
